import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Check, Award, FileText, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LanguageAwareLink from "@/components/LanguageAwareLink";

const NgocMinhCase = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-maitech-900">
      <Helmet>
        <title>Digital and AI Transformation for Ngoc Minh Medical Center | MAI Tech Case Study</title>
        <meta name="description" content="How MAI Tech implemented AI-powered solutions for Ngoc Minh Medical Center, boosting productivity and enhancing patient experience." />
        <meta property="og:title" content="Digital and AI Transformation for Ngoc Minh Medical Center | MAI Tech Case Study" />
        <meta property="og:description" content="How we helped a leading medical center implement AI solutions for customer service, multilingual website, and patient care." />
        <meta property="og:image" content="/clients/ngocminh image.png" />
        <meta property="og:type" content="article" />
      </Helmet>

      <Navbar />
      
      <div className="container mx-auto px-4 py-16">
        <div className="mb-8">
          <LanguageAwareLink to="/case-studies" className="text-primary flex items-center hover:underline">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to MAI Tech Solutions
          </LanguageAwareLink>
        </div>

        <header className="mb-16">
          <div className="flex items-center mb-3">
            <span className="text-sm bg-primary/20 text-primary px-3 py-1 rounded-full">
              Healthcare
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-6">Digital and AI Transformation for Ngoc Minh Medical Center</h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            How we revolutionized patient care and streamlined operations at a leading medical center through AI-powered solutions.
          </p>
        </header>

        <div className="mb-16">
          <img 
            src="/clients/ngocminh image.png" 
            alt="Ngoc Minh Medical Center"
            className="w-full h-auto rounded-lg shadow-xl object-contain max-h-[500px]"
          />
        </div>

        <div className="grid md:grid-cols-3 gap-12 mb-16">
          <div className="bg-maitech-800 p-6 rounded-lg border border-maitech-700">
            <div className="flex items-center mb-4">
              <Award className="h-6 w-6 text-primary mr-3" />
              <h3 className="text-xl font-semibold">Key Results</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check className="text-primary mt-1 mr-2 h-5 w-5 flex-shrink-0" />
                <span className="text-gray-200">40% increase in customer service efficiency</span>
              </li>
              <li className="flex items-start">
                <Check className="text-primary mt-1 mr-2 h-5 w-5 flex-shrink-0" />
                <span className="text-gray-200">30% boost in appointment bookings</span>
              </li>
              <li className="flex items-start">
                <Check className="text-primary mt-1 mr-2 h-5 w-5 flex-shrink-0" />
                <span className="text-gray-200">25% reduction in administrative workload</span>
              </li>
              <li className="flex items-start">
                <Check className="text-primary mt-1 mr-2 h-5 w-5 flex-shrink-0" />
                <span className="text-gray-200">45% improvement in patient satisfaction ratings</span>
              </li>
              <li className="flex items-start">
                <Check className="text-primary mt-1 mr-2 h-5 w-5 flex-shrink-0" />
                <span className="text-gray-200">20% growth in international patient visits</span>
              </li>
            </ul>
          </div>

          <div className="bg-maitech-800 p-6 rounded-lg border border-maitech-700">
            <div className="flex items-center mb-4">
              <FileText className="h-6 w-6 text-primary mr-3" />
              <h3 className="text-xl font-semibold">Project Details</h3>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-gray-400 text-sm">Implementation Time</p>
                <p className="text-white">12 weeks</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">ROI Period</p>
                <p className="text-white">5 months</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Technologies Used</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {["AI Chatbot", "Mobile App Development", "Web Development", "Natural Language Processing", "Multilingual Support"].map((tech, idx) => (
                    <span 
                      key={idx} 
                      className="bg-maitech-700/70 px-2 py-1 rounded-md text-xs text-gray-200"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-maitech-800 p-6 rounded-lg border border-maitech-700">
            <div className="flex items-center mb-4">
              <svg className="h-6 w-6 text-primary mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
              </svg>
              <h3 className="text-xl font-semibold">Client Testimonial</h3>
            </div>
            <blockquote className="text-gray-300 italic mb-4">
              "The integration of MAI Tech's solutions has completely transformed our medical center. The AI-powered tools have not only enhanced our operational efficiency but have significantly improved the quality of care we provide to our patients."
            </blockquote>
            <div>
              <p className="text-white font-medium">Dr. Minh Nguyen</p>
              <p className="text-gray-400 text-sm">Medical Director</p>
              <p className="text-gray-400 text-sm">Ngoc Minh Medical Center</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-2xl font-bold mb-6 text-white">The Challenge</h2>
            <div className="prose-article bg-maitech-800/50 backdrop-blur rounded-lg p-6 border border-maitech-700/50">
              <p>
                Ngoc Minh Medical Center, a leading healthcare provider in Vietnam, was facing challenges in managing the increasing number of patients while maintaining high-quality care. Their traditional appointment booking system was becoming inefficient, leading to longer wait times and patient frustration. Additionally, they needed to expand their reach to international patients but were limited by language barriers and outdated digital infrastructure.
              </p>
            </div>
          </div>
          
          <div>
            <h2 className="text-2xl font-bold mb-6 text-white">Our Solution</h2>
            <div className="prose-article bg-maitech-800/50 backdrop-blur rounded-lg p-6 border border-maitech-700/50">
              <p>
                We developed a comprehensive digital transformation strategy for Ngoc Minh Medical Center that included:
              </p>
              <ul className="list-disc pl-5 space-y-2 mt-3">
                <li>Implementation of MAI Bot for customer service and sales, offering instant responses to patient inquiries</li>
                <li>Development of a modern, responsive website in 4 languages to cater to local and international patients</li>
                <li>Creation of an AI doctor assistant to provide general medical advice to patients</li>
                <li>Mobile application for appointment scheduling, accessing medical records, viewing upcoming appointments, and managing prescriptions</li>
                <li>Integrated administrative dashboard for staff to manage patient flow efficiently</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold mb-6 text-white">Implementation Gallery</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-maitech-800/50 p-4 rounded-lg border border-maitech-700">
              <img 
                src="/clients/ngocminh3.png" 
                alt="Ngoc Minh implementation 1"
                className="w-full h-auto rounded-lg object-contain aspect-auto"
              />
            </div>
            <div className="bg-maitech-800/50 p-4 rounded-lg border border-maitech-700">
              <img 
                src="/clients/ngocminh2.png" 
                alt="Ngoc Minh implementation 2"
                className="w-full h-auto rounded-lg object-contain aspect-auto"
              />
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold mb-6 text-white">Implementation Process</h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 h-full w-0.5 bg-maitech-700 transform -translate-x-1/2"></div>
            
            <div className="space-y-12 relative">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 md:pr-8 md:text-right mb-4 md:mb-0">
                  <h3 className="text-xl font-semibold text-primary">Needs Assessment</h3>
                  <p className="text-gray-300 mt-2">
                    Conducted comprehensive analysis of current workflows, patient journey, and pain points in the medical center's operations.
                  </p>
                </div>
                <div className="flex items-center justify-center md:absolute md:left-1/2 md:transform md:-translate-x-1/2">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold">1</div>
                </div>
                <div className="md:w-1/2 md:pl-8 ml-12 md:ml-0"></div>
              </div>
              
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 md:pr-8 md:text-right mb-4 md:mb-0 ml-12 md:ml-0"></div>
                <div className="flex items-center justify-center md:absolute md:left-1/2 md:transform md:-translate-x-1/2">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold">2</div>
                </div>
                <div className="md:w-1/2 md:pl-8 ml-12 md:ml-0">
                  <h3 className="text-xl font-semibold text-primary">Technology Design</h3>
                  <p className="text-gray-300 mt-2">
                    Designed and prototyped the multilingual website, mobile app, AI doctor, and MAI Bot integration for optimal user experience.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 md:pr-8 md:text-right mb-4 md:mb-0">
                  <h3 className="text-xl font-semibold text-primary">AI Integration</h3>
                  <p className="text-gray-300 mt-2">
                    Implemented and trained the MAI Bot and AI doctor with medical knowledge and conversational abilities to handle patient inquiries.
                  </p>
                </div>
                <div className="flex items-center justify-center md:absolute md:left-1/2 md:transform md:-translate-x-1/2">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold">3</div>
                </div>
                <div className="md:w-1/2 md:pl-8 ml-12 md:ml-0"></div>
              </div>
              
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 md:pr-8 md:text-right mb-4 md:mb-0 ml-12 md:ml-0"></div>
                <div className="flex items-center justify-center md:absolute md:left-1/2 md:transform md:-translate-x-1/2">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold">4</div>
                </div>
                <div className="md:w-1/2 md:pl-8 ml-12 md:ml-0">
                  <h3 className="text-xl font-semibold text-primary">Multilingual Development</h3>
                  <p className="text-gray-300 mt-2">
                    Developed the website and applications in 4 languages to serve both local and international patients effectively.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 md:pr-8 md:text-right mb-4 md:mb-0">
                  <h3 className="text-xl font-semibold text-primary">Staff Training & Launch</h3>
                  <p className="text-gray-300 mt-2">
                    Provided comprehensive training to medical staff and launched the solutions with continuous support and optimization.
                  </p>
                </div>
                <div className="flex items-center justify-center md:absolute md:left-1/2 md:transform md:-translate-x-1/2">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold">5</div>
                </div>
                <div className="md:w-1/2 md:pl-8 ml-12 md:ml-0"></div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 mb-6 flex justify-center">
          <div className="max-w-sm">
            <a href="https://pkdkngocminh.com.vn/" target="_blank" rel="noopener noreferrer">
              <img 
                src="/clients/ngocminh image.png"
                alt="Ngoc Minh Medical Center Logo"
                className="w-full h-auto object-contain"
              />
            </a>
          </div>
        </div>

        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold mb-6 text-white">Ready to Transform Your Healthcare Practice?</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how our AI and digital solutions can help your healthcare organization enhance patient care and operational efficiency.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <LanguageAwareLink to="/contact">
              <Button>
                Contact Us
              </Button>
            </LanguageAwareLink>
            <LanguageAwareLink to="/products">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary/10">
                Explore Our Products
              </Button>
            </LanguageAwareLink>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default NgocMinhCase; 